/**
 * @fileoverview Protected Route Component
 * @description Route wrapper component that provides authentication and role-based
 * access control for protected pages and components.
 * @author Production Dashboard Team
 * @since 1.0.0
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * @typedef {Object} ProtectedRouteProps
 * @description Props for the ProtectedRoute component
 * @property {React.ReactNode} children - Child components to render if authorized
 * @property {string|string[]|null} [requiredRole=null] - Required role(s) for access
 */

/**
 * ProtectedRoute Component
 * @description Wrapper component that handles authentication and role-based access
 * control. Redirects unauthenticated users to login and shows access denied for
 * users without required roles.
 * 
 * @component
 * @param {ProtectedRouteProps} props - Component props
 * @returns {JSX.Element|React.ReactNode} Protected content or redirect/error UI
 * 
 * @example
 * // Basic authentication protection
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 * 
 * @example
 * // Single role requirement
 * <ProtectedRoute requiredRole="admin">
 *   <AdminPanel />
 * </ProtectedRoute>
 * 
 * @example
 * // Multiple role requirements
 * <ProtectedRoute requiredRole={['manager', 'admin']}>
 *   <ProductionDashboard />
 * </ProtectedRoute>
 */
const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { isAuthenticated, userType, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login with return path
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If specific role is required and user doesn't have it
  if (requiredRole) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    const hasRequiredRole = roles.includes(userType);

    if (!hasRequiredRole) {
      return (
        <div className="access-denied-container">
          <div className="access-denied">
            <h2>Access Denied</h2>
            <p>You don't have permission to access this page.</p>
            <p>Required role(s): {roles.join(', ')}</p>
            <p>Your role: {userType || 'No role assigned'}</p>
          </div>
        </div>
      );
    }
  }

  // If authenticated and authorized, render children
  return children;
};

// Higher-order component for wrapping components with protection
export const withProtectedRoute = (Component, requiredRole = null) => {
  return function ProtectedComponent(props) {
    return (
      <ProtectedRoute requiredRole={requiredRole}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
};

export default ProtectedRoute;